/**
 * 来場者ログイン画面
 * ユーザ名とパスワード入力後に来場者ホーム（QRで開いた画面から来た場合はQRの続き）へ遷移します
 */

import React, { useCallback, useMemo, useRef, useState } from 'react';
import { ScrollView, Text, TextInput, View } from '../../../shared/components/WebPrimitives';
import {
  LOGIN_BUTTON_LABEL,
  LOGIN_BUTTON_LOADING_LABEL,
  VISITOR_LOGIN_RETURN_TO_QR_GUIDANCE_MESSAGE,
} from '../constants';
import VisitorAuthModeSwitch from '../components/VisitorAuthModeSwitch';
import { useVisitorLoginForm } from '../hooks/useVisitorLoginForm';
import { resolveLoginReturnDestination } from '../utils/loginReturnDestination';
import { VISITOR_ROUTES } from '../../../navigation/routeNames';
import AppButton from '../../../shared/components/AppButton';
import PasswordVisibilityToggleButton from '../../../shared/components/PasswordVisibilityToggleButton';
import ScreenLayout from '../../../shared/components/ScreenLayout';
import StorageUnavailableNotice from '../../../shared/components/StorageUnavailableNotice';
import TextSizeControl from '../../../shared/components/TextSizeControl';
import usePublicAppSettings from '../../../shared/hooks/usePublicAppSettings';
import {
  VISITOR_LOGIN_USERNAME_MAX_LENGTH,
  VISITOR_PASSWORD_MAX_LENGTH,
  countVisitorUsernameCharacters,
} from '../../../shared/utils/validation';
import { INPUT_PLACEHOLDER_COLOR } from '../../../shared/constants/accessibility';

/** ログインフォームのエラー説明ID */
const LOGIN_FORM_ERROR_ID = 'visitor-login-form-error';

/**
 * セッション消失時の案内文を生成する
 * @param {{ exchangePlaceLabel: string, supportContactLabel: string }} settings - 公開アプリ設定
 * @returns {string} 案内文
 */
const createSessionLostGuidanceMessage = (settings) => {
  return `ログイン状態が消えた場合は、${settings.exchangePlaceLabel}または${settings.supportContactLabel}までお越しください。`;
};

/**
 * 来場者ログイン画面
 * @param {Object} props - プロパティ
 * @param {Object} props.navigation - ナビゲーション
 * @returns {JSX.Element} 来場者ログイン画面
 */
const VisitorLoginScreen = ({ navigation, route }) => {
  /** 公開アプリ設定 */
  const { settings } = usePublicAppSettings();

  /** ログインフォーム状態 */
  const {
    username,
    password,
    errorMessage,
    isSubmitting,
    handleUsernameChange,
    handlePasswordChange,
    submitLogin,
  } =
    useVisitorLoginForm();

  /** ユーザ名入力要素 */
  const usernameInputRef = useRef<HTMLInputElement>(null);

  /** パスワード入力要素 */
  const passwordInputRef = useRef<HTMLInputElement>(null);

  /** パスワードを画面表示中か（既定は非表示） */
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);

  /**
   * パスワードの表示・非表示を切り替える
   * @returns {void}
   */
  const handleTogglePasswordVisibility = useCallback(() => {
    setIsPasswordVisible((previous) => !previous);
  }, []);

  /** ログイン後に戻るQRの画面 */
  const returnDestination = useMemo(
    () => resolveLoginReturnDestination(route?.params?.returnTo),
    [route?.params?.returnTo],
  );

  /** セッション消失案内表示可否 */
  const shouldShowSessionLostGuidance = Boolean(route?.params?.sessionLost);

  /** セッション消失案内文 */
  const sessionLostGuidanceMessage = createSessionLostGuidanceMessage(settings);

  /**
   * 最初の不正入力へDOM更新後にfocusする
   * @param {'username' | 'password' | undefined} invalidField - 不正入力名
   * @returns {void}
   */
  const focusInvalidInput = useCallback((invalidField) => {
    /** focus対象input ref */
    const inputRef = invalidField === 'username'
      ? usernameInputRef
      : invalidField === 'password'
        ? passwordInputRef
        : null;

    if (!inputRef) {
      return;
    }

    window.requestAnimationFrame(() => {
      inputRef.current?.focus();
    });
  }, []);

  /** 入力値で来場者ホームへ遷移する */
  const handlePressLogin = useCallback(async () => {
    /** ログイン送信結果 */
    const submitResult = await submitLogin();

    if (!submitResult.isSuccess || !submitResult.payload) {
      focusInvalidInput(submitResult.invalidField);
      return;
    }

    // セッション情報はlocalStorageに保存済みのため、URLへ秘密情報を渡さない
    if (returnDestination) {
      // QRの続きへ戻り、戻る操作でログイン画面を開き直さないよう履歴を置き換える
      navigation.replace(returnDestination.name, returnDestination.params);
      return;
    }

    navigation.navigate(VISITOR_ROUTES.HOME);
  }, [focusInvalidInput, navigation, returnDestination, submitLogin]);

  /** 新規登録画面へ遷移する（QRの戻り先は持ったまま） */
  const handlePressGoToRegister = useCallback(() => {
    if (returnDestination) {
      navigation.navigate(VISITOR_ROUTES.REGISTER, { returnTo: returnDestination });
      return;
    }

    navigation.navigate(VISITOR_ROUTES.REGISTER);
  }, [navigation, returnDestination]);

  /** ユーザ名入力文字数 */
  const usernameLength = countVisitorUsernameCharacters(username.trim());

  return (
    <ScreenLayout
      badge="冒険のはじまり"
      description="ユーザ名とパスワードを入力して冒険を開始してください。"
      mascotMood={shouldShowSessionLostGuidance ? 'trouble' : 'focus'}
      mascotPose="guide"
      theme="visitor"
      title="来場者ログイン"
    >
      <ScrollView
        className="flex-1"
        keyboardShouldPersistTaps="handled"
        showsVerticalScrollIndicator={false}
        testID="visitor-login-scroll-view"
      >
        <View className="gap-4 pb-8">
        <VisitorAuthModeSwitch current="login" onSwitch={handlePressGoToRegister} />

        <StorageUnavailableNotice />

        {returnDestination ? (
          <View className="border-2 border-app-quest-night/20 bg-[#FFF8DD] p-3" testID="visitor-login-return-to-qr-guidance">
            <Text className="text-sm leading-5 text-app-text">
              {VISITOR_LOGIN_RETURN_TO_QR_GUIDANCE_MESSAGE}
            </Text>
          </View>
        ) : null}

        <View>
          <Text className="mb-2 text-sm font-medium text-app-text">ユーザ名</Text>
          <TextInput
            accessibilityLabel="ユーザ名"
            aria-describedby={errorMessage ? LOGIN_FORM_ERROR_ID : undefined}
            aria-invalid={Boolean(errorMessage)}
            autoCapitalize="none"
            autoComplete="username"
            autoCorrect={false}
            className="rounded-none border-2 border-app-border bg-[#FFFDF0] px-4 py-4 text-base text-app-text"
            maxLength={VISITOR_LOGIN_USERNAME_MAX_LENGTH}
            onChangeText={handleUsernameChange}
            placeholder={settings.visitorUsernamePlaceholder}
            placeholderTextColor={INPUT_PLACEHOLDER_COLOR}
            ref={usernameInputRef}
            value={username}
          />
          <Text className="mt-2 text-right text-xs text-app-text-secondary">
            {usernameLength}/{VISITOR_LOGIN_USERNAME_MAX_LENGTH}
          </Text>
        </View>

        <View>
          <Text className="mb-2 text-sm font-medium text-app-text">パスワード</Text>
          <View className="flex-row items-center gap-2">
            <TextInput
              accessibilityLabel="パスワード"
              aria-describedby={errorMessage ? LOGIN_FORM_ERROR_ID : undefined}
              aria-invalid={Boolean(errorMessage)}
              autoCapitalize="none"
              autoComplete="current-password"
              autoCorrect={false}
              className="min-w-0 flex-1 rounded-none border-2 border-app-border bg-[#FFFDF0] px-4 py-4 text-base text-app-text"
              maxLength={VISITOR_PASSWORD_MAX_LENGTH}
              onChangeText={handlePasswordChange}
              placeholder="パスワード"
              placeholderTextColor={INPUT_PLACEHOLDER_COLOR}
              ref={passwordInputRef}
              secureTextEntry={!isPasswordVisible}
              value={password}
            />
            <PasswordVisibilityToggleButton
              isVisible={isPasswordVisible}
              onToggle={handleTogglePasswordVisibility}
              testID="visitor-login-password-visibility-toggle"
            />
          </View>
          <Text className="mt-2 text-xs leading-5 text-app-text-secondary">
            忘れた場合は{settings.supportContactLabel}へ相談してください。
          </Text>
        </View>

        {errorMessage ? (
          <Text
            accessibilityLiveRegion="assertive"
            accessibilityRole="alert"
            className="text-sm font-medium text-app-danger"
            id={LOGIN_FORM_ERROR_ID}
          >
            {errorMessage}
          </Text>
        ) : null}

        {shouldShowSessionLostGuidance ? (
          <View className="rounded-none border-2 border-app-danger bg-red-50 p-3">
            <Text className="text-sm font-medium text-app-danger">
              {sessionLostGuidanceMessage}
            </Text>
          </View>
        ) : null}

        <AppButton
          disabled={isSubmitting}
          label={isSubmitting ? LOGIN_BUTTON_LOADING_LABEL : LOGIN_BUTTON_LABEL}
          onPress={handlePressLogin}
        />

        <TextSizeControl />

        </View>
      </ScrollView>
    </ScreenLayout>
  );
};

export default VisitorLoginScreen;
